import type { PromoCode as PromoCodeType, PromoCodeDiscount, PackagePrices } from 'types/join';

/**
 * Converts technical error messages to user-friendly messages
 * @param error - The error object or string from the API
 * @param defaultMessage - Default message to return if no specific error is found
 * @returns User-friendly error message
 */
export const getErrorMessage = (
  error: any,
  defaultMessage = 'Invalid promo code or not applicable.'
): string => {
  if (!error) return defaultMessage;

  // Handle status-based responses (when API returns status field)
  if (typeof error === 'object' && error.status) {
    switch (error.status) {
      case 'NotFound':
        return 'Promo code not found.';
      case 'Expired':
        return 'Promo code has expired.';
      case 'ExceededUsage':
        return 'This promo code is no longer available.';
      default:
        return error.message || defaultMessage;
    }
  }

  // If it's already a user-friendly string, return it
  if (typeof error === 'string') {
    // Check if it's a JSON string that needs parsing
    if (error.startsWith('{') && error.includes('errorCode')) {
      try {
        const parsedError = JSON.parse(error);
        if (parsedError.errorMessage) {
          // Handle specific error codes with user-friendly messages
          if (parsedError.errorCode === 500) {
            return 'Service temporarily unavailable. Please try again later.';
          }
          if (parsedError.errorCode === 404) {
            return 'Promo code not found. Please check the code and try again.';
          }
          if (parsedError.errorCode === 400) {
            return 'Invalid promo code format. Please check the code and try again.';
          }
          // For other error codes, return a generic message
          return 'Invalid promo code or not applicable.';
        }
      } catch (e) {
        // If JSON parsing fails, continue to other checks
      }
    }

    // Check for other error patterns in the string
    if (error.includes('404') || error.includes('not found')) {
      return 'Promo code not found. Please check the code and try again.';
    }
    if (error.includes('400') || error.includes('bad request')) {
      return 'Invalid promo code format. Please check the code and try again.';
    }
    if (error.includes('500') || error.includes('internal server error')) {
      return 'Service temporarily unavailable. Please try again later.';
    }
    if (error.includes('expired') || error.includes('invalid')) {
      return 'This promo code is no longer valid.';
    }

    return error;
  }

  // Try to parse JSON error response for object errors
  try {
    if (typeof error === 'object') {
      if (error.message) {
        return error.message;
      }
      if (error.errorMessage) {
        return error.errorMessage;
      }
      if (error.errorCode) {
        // Handle specific error codes
        if (error.errorCode === 500) {
          return 'Service temporarily unavailable. Please try again later.';
        }
        if (error.errorCode === 404) {
          return 'Promo code not found. Please check the code and try again.';
        }
        if (error.errorCode === 400) {
          return 'Invalid promo code format. Please check the code and try again.';
        }
        return 'Invalid promo code or not applicable.';
      }
    }

    // Try to parse as JSON string
    const parsedError = JSON.parse(error);
    if (parsedError.message) {
      return parsedError.message;
    }
    if (parsedError.errorMessage) {
      return parsedError.errorMessage;
    }
    if (parsedError.errorCode) {
      // Handle specific error codes
      if (parsedError.errorCode === 500) {
        return 'Service temporarily unavailable. Please try again later.';
      }
      if (parsedError.errorCode === 404) {
        return 'Promo code not found. Please check the code and try again.';
      }
      if (parsedError.errorCode === 400) {
        return 'Invalid promo code format. Please check the code and try again.';
      }
      return 'Invalid promo code or not applicable.';
    }
  } catch (e) {
    // If JSON parsing fails, return default error message
  }

  // Default user-friendly error message
  return defaultMessage;
};

/**
 * Checks if a promo code is valid but not applicable for the current selection
 * @param promoCode - The promo code data from API
 * @param selectedPlan - The selected main plan
 * @param selectedAddons - Array of selected addons
 * @param userCountry - User's country for validation
 * @returns Object with isApplicable flag and reason if not applicable
 *
 * Special handling for Rapnet and Pricelist subscription types:
 * - If promo code has subscriptionType of 'Rapnet' or 'Pricelist', it will only apply
 *   when the selected plan has the same subscriptionType
 * - This check happens before package ID validation
 * - All other logic (countries, packageIds) remains intact
 */
export const checkPromoCodeApplicability = (
  promoCode: PromoCodeType,
  selectedPlan: PackagePrices | undefined,
  selectedAddons: (PackagePrices | undefined)[] | undefined,
  userCountry = 'USA'
): { isApplicable: boolean; reason?: string } => {
  if (!selectedPlan?.period?.toLowerCase().includes('year')) {
    return {
      isApplicable: false,
      reason: "The promo code isn't available for the Monthly plan."
    };
  }
  // Check if user's country is in the allowed countries
  if (promoCode.countries && promoCode.countries.length > 0) {
    if (!promoCode.countries.includes(userCountry)) {
      return {
        isApplicable: false,
        reason: "This Promo Code isn't available in the country you selected."
      };
    }
  }

  // Check subscription type compatibility for Rapnet and Pricelist
  if (promoCode.subscriptionType) {
    const subscriptionType = promoCode.subscriptionType.toLowerCase();
    if (subscriptionType === 'rapnet' || subscriptionType === 'pricelist') {
      if (!selectedPlan || selectedPlan.subscriptionType.toLowerCase() !== subscriptionType) {
        return {
          isApplicable: false,
          reason: "This Promo Code isn't available for the selected package/s."
        };
      }
      // If subscription types match, apply to main package and addons (subject to package ID restrictions)
    }
  }

  // If no package IDs are specified, the promo code applies to all packages
  if (!promoCode.packageIds || promoCode.packageIds.length === 0) {
    return { isApplicable: true };
  }

  // Check if the selected plan and addons are in the allowed package IDs
  const selectedPackageIds = [
    selectedPlan?.packageId,
    ...(selectedAddons?.map(addon => addon?.packageId).filter(Boolean) || [])
  ].filter(Boolean) as string[];

  const applicablePackages = selectedPackageIds.filter(packageId =>
    promoCode.packageIds.includes(packageId)
  );

  if (applicablePackages.length === 0) {
    return {
      isApplicable: false,
      reason: "This Promo Code isn't available for the selected package/s."
    };
  }

  return { isApplicable: true };
};

/**
 * Calculates discount based on promo code and selected packages
 * @param promoCode - The promo code data from API
 * @param selectedPlan - The selected main plan
 * @param selectedAddons - Array of selected addons
 * @param userCountry - User's country for validation
 * @returns PromoCodeDiscount object or null if not applicable
 *
 * Special handling for Rapnet and PriceList subscription types:
 * - If promo code has subscriptionType of 'Rapnet' or 'Pricelist', it will only apply
 *   when the selected plan has the same subscriptionType
 * - This check happens before package ID validation
 * - All other logic (countries, packageIds) remains intact
 */
export const calculatePromoCodeDiscount = (
  promoCode: PromoCodeType,
  selectedPlan: PackagePrices | undefined,
  selectedAddons: (PackagePrices | undefined)[] | undefined,
  userCountry: string
): PromoCodeDiscount | null => {
  if (!selectedPlan?.period?.toLowerCase().includes('year')) {
    return null;
  }
  // Check if user's country is in the allowed countries
  if (promoCode.countries && promoCode.countries.length > 0) {
    if (!promoCode.countries.includes(userCountry)) {
      return null; // Country not allowed
    }
  }

  // Check subscription type compatibility for Rapnet and Pricelist
  if (promoCode.subscriptionType) {
    const subscriptionType = promoCode.subscriptionType.toLowerCase();
    if (subscriptionType === 'rapnet' || subscriptionType === 'pricelist') {
      if (!selectedPlan || selectedPlan.subscriptionType.toLowerCase() !== subscriptionType) {
        return null; // Subscription type doesn't match
      }
      // If subscription types match, apply to main package and addons (subject to package ID restrictions)
    }
  }

  // Get all selected package IDs, filtering out packages with price 0
  const selectedPackageIds = [
    selectedPlan?.packageId,
    ...(selectedAddons?.map(addon => addon?.packageId).filter(Boolean) || [])
  ].filter(Boolean) as string[];

  // Filter out packages with price 0 before calculating discounts
  const packagesWithPrices = selectedPackageIds.filter(packageId => {
    if (packageId === selectedPlan?.packageId) {
      return selectedPlan?.price && selectedPlan.price > 0;
    } else {
      const addon = selectedAddons?.find(a => a?.packageId === packageId);
      return addon?.price && addon.price > 0;
    }
  });

  // If no package IDs are specified, the promo code applies to all packages with prices > 0
  let applicablePackages: string[];
  if (!promoCode.packageIds || promoCode.packageIds.length === 0) {
    applicablePackages = packagesWithPrices;
  } else {
    // Check if the selected plan and addons are in the allowed package IDs and have prices > 0
    applicablePackages = packagesWithPrices.filter(packageId =>
      promoCode.packageIds.includes(packageId)
    );
  }

  if (applicablePackages.length === 0) {
    return null; // No applicable packages
  }

  // Calculate discount amount
  let discountAmount = 0;
  if (promoCode.discountPercent) {
    // Calculate percentage discount for each applicable package
    // Round the final price after discount: Round(packagePrice - (packagePrice * discountPercent))
    discountAmount = packagesWithPrices.reduce((total, packageId) => {
      // Check if this package is applicable (either no restrictions or in allowed list)
      const isApplicable =
        !promoCode.packageIds ||
        promoCode.packageIds.length === 0 ||
        promoCode.packageIds.includes(packageId);

      if (isApplicable) {
        if (packageId === selectedPlan?.packageId) {
          // For main plan, calculate final price after discount and round
          const packagePrice = selectedPlan?.price || 0;
          // Skip if price is 0
          if (packagePrice <= 0) return total;

          const discountPercentDecimal = (promoCode.discountPercent ?? 0) / 100;
          const finalPriceAfterDiscount = Math.round(
            packagePrice - packagePrice * discountPercentDecimal
          );
          const packageDiscount = packagePrice - finalPriceAfterDiscount;
          return total + packageDiscount;
        } else {
          // For addons, calculate final price after discount per unit, then multiply by count
          const addon = selectedAddons?.find(a => a?.packageId === packageId);
          const addonPrice = addon?.price || 0;
          const addonCount = addon?.addonCount || 0;

          // Skip if price is 0
          if (addonPrice <= 0) return total;

          // Calculate final price after discount for one unit and round
          const discountPercentDecimal = (promoCode.discountPercent ?? 0) / 100;
          const finalPriceAfterDiscount = Math.round(
            addonPrice - addonPrice * discountPercentDecimal
          );
          const unitDiscount = addonPrice - finalPriceAfterDiscount;
          // Then multiply by the addon count
          const totalAddonDiscount = unitDiscount * addonCount;
          return total + totalAddonDiscount;
        }
      }
      return total;
    }, 0);
  } else if (promoCode.discountAmount) {
    // Apply discount amount per package instance (including addon counts)
    discountAmount = packagesWithPrices.reduce((total, packageId) => {
      // Check if this package is applicable (either no restrictions or in allowed list)
      const isApplicable =
        !promoCode.packageIds ||
        promoCode.packageIds.length === 0 ||
        promoCode.packageIds.includes(packageId);

      if (isApplicable) {
        if (packageId === selectedPlan?.packageId) {
          // For main plan, apply discount amount once
          const packagePrice = selectedPlan?.price || 0;
          // Skip if price is 0
          if (packagePrice <= 0) return total;

          if (packagePrice < (promoCode.discountAmount ?? 0)) {
            return total + packagePrice;
          }
          return total + (promoCode.discountAmount ?? 0);
        } else {
          // For addons, apply discount amount per addon count
          const addon = selectedAddons?.find(a => a?.packageId === packageId);
          const addonPrice = addon?.price || 0;
          const addonCount = addon?.addonCount || 1;

          // Skip if price is 0
          if (addonPrice <= 0) return total;

          if (addonPrice < (promoCode.discountAmount ?? 0)) {
            return total + addonPrice * addonCount;
          }
          return total + (promoCode.discountAmount ?? 0) * addonCount;
        }
      }
      return total;
    }, 0);
  }

  return {
    promoCodeId: promoCode.promoCodeId,
    promoCodeName: promoCode.promoCodeName,
    discountAmount,
    discountPercent: promoCode.discountPercent,
    appliedToPackages: applicablePackages
  };
};
